
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { 
  getAllUsers, 
  searchUsers, 
  updateUserProfile, 
  deleteUserAccount,
  suspendUserAccount,
  banUserAccount,
  reactivateUserAccount,
  checkIsAdmin,
  AdminUser 
} from '../services/userManagementService';
import { toast } from 'sonner';

export const useUserManagement = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await checkIsAdmin();
      setIsAdmin(adminStatus);
    };
    checkAdminStatus();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users', debouncedSearchTerm],
    queryFn: () => debouncedSearchTerm.trim() ? searchUsers(debouncedSearchTerm) : getAllUsers(),
    enabled: isAdmin,
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, updates }: { 
      userId: string; 
      updates: {
        full_name?: string;
        subscription_tier?: 'free' | 'standard' | 'premium' | 'ultimate';
        admin_role?: string;
        premium_trial_ends_at?: string | null;
        subscription_ends_at?: string | null;
        custom_pet_limit?: number | null;
        custom_medical_record_limit?: number | null;
      }
    }) => updateUserProfile(userId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(t('adminPanel.toasts.userUpdated'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('adminPanel.toasts.errorUpdate'));
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUserAccount(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(t('adminPanel.toasts.userDeleted'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('adminPanel.toasts.errorDelete'));
    },
  });

  const suspendUserMutation = useMutation({
    mutationFn: ({ userId, reason, suspendUntil }: { 
      userId: string; 
      reason: string; 
      suspendUntil?: Date 
    }) => suspendUserAccount(userId, reason, suspendUntil),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(t('adminPanel.toasts.userSuspended'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('adminPanel.toasts.errorSuspend'));
    },
  });

  const banUserMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) => 
      banUserAccount(userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(t('adminPanel.toasts.userBanned'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('adminPanel.toasts.errorBan'));
    },
  });

  const reactivateUserMutation = useMutation({
    mutationFn: (userId: string) => reactivateUserAccount(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(t('adminPanel.toasts.userReactivated'));
    },
    onError: (error: any) => {
      toast.error(error.message || t('adminPanel.toasts.errorReactivate'));
    },
  });

  return {
    users: users || [],
    isLoading,
    error,
    isAdmin,
    searchTerm,
    setSearchTerm,
    updateUser: updateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
    suspendUser: suspendUserMutation.mutate,
    banUser: banUserMutation.mutate,
    reactivateUser: reactivateUserMutation.mutate,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isSuspending: suspendUserMutation.isPending,
    isBanning: banUserMutation.isPending,
    isReactivating: reactivateUserMutation.isPending,
  };
};
